import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { GatewayCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/outputs/gateway-customer.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetCustomerQuery } from '@app/core/service/cpms-bo-customer.service';
import { CustomerUpdateController } from '@app/feature/customer/customer-update/customer-update.controller';
import { useSetPageTitleContext } from '@app/shared/layout/page-layout/page-layout-title.context-provider';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';

import { CustomerContextProvider } from '../customer.context';
import { CustomerDetailsLoadingView } from './customer-details-loading.view';
import { CustomerDetailsView } from './customer-details.view';

interface CustomerDetailsControllerProps {
  resource: GatewayCustomerModel;
}

const CustomerDetailsController = (
  props: CustomerDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.name);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CUSTOMERS,
        action: ActionsByResource.CUSTOMERS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="update"
          onClick={() => setActionUpdateIsDialogOpen(true)}
          icon="edit-05"
        />,
      );
    }
    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CUSTOMERS,
        action: ActionsByResource.CUSTOMERS.DELETE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="delete"
          icon="trash-01"
          onClick={() => setActionUpdateIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <CustomerContextProvider data={resource}>
      <CustomerDetailsView />
      <Dialog
        open={actionUpdateIsDialogOpen}
        size={DialogSize.LARGE}
        height="100%"
        confirmClose
        onClose={() => setActionUpdateIsDialogOpen(false)}>
        <Dialog.Header
          icon="dataflow-04"
          onClose={() => setActionUpdateIsDialogOpen(false)}
          title={t('$.customer-update.title')}
        />
        <CustomerUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </CustomerContextProvider>
  );
};

export const CustomerDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetCustomerQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayCustomerModel,
    {},
    GatewayCustomerModel
  >(CustomerDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.CUSTOMERS,
        action: ActionsByResource.CUSTOMERS.READ,
      }),
    loadingComponent: <CustomerDetailsLoadingView />,
  })({});

  return Component;
};
